import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaShoppingCart } from 'react-icons/fa';
import { Utensils } from 'lucide-react';
import { getDisplayRating } from '../../utils/ratingStars';
import namkeFallback from '../../assets/namke-fallback.png';

const MenuDetailPopup = ({
  selectedMenu,
  closeMenuPopup,
  renderStars,
  formatPrice,
  getImageUrl,
  handleAddToCart
}) => {
  const { t } = useTranslation();
  if (!selectedMenu) return null;
  const displayRating = getDisplayRating(selectedMenu, 0);

  const imageUrl = getImageUrl
    ? getImageUrl(selectedMenu.image_url || selectedMenu.dish_image_url || (selectedMenu.dishes?.[0]?.dish_image_url || selectedMenu.dishes?.[0]?.image_url))
    : selectedMenu.image_url || selectedMenu.dish_image_url || namkeFallback;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '640px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px', letterSpacing: '-0.02em' }}>{selectedMenu.name}</h2>
          <button
            type="button"
            onClick={closeMenuPopup}
            style={{ flexShrink: 0, border: 'none', cursor: 'pointer', width: '38px', height: '38px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div style={{ padding: '26px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ height: '240px', borderRadius: '22px', overflow: 'hidden' }}>
            <img
              src={imageUrl}
              alt={selectedMenu.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = namkeFallback;
              }}
            />
          </div>

          {selectedMenu.description && (
            <p style={{ margin: 0, fontSize: '15.5px', lineHeight: 1.65, color: 'rgba(5,16,4,0.8)' }}>
              {selectedMenu.description}
            </p>
          )}

          <div className="flex items-center" style={{ marginTop: '-8px' }}>
            {renderStars && renderStars(displayRating)}
            <span style={{ marginLeft: '8px', fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>
              {t('restaurant.reviewCountParens', { count: selectedMenu.reviews ?? 0 })}
            </span>
          </div>

          <div>
            <h3 style={{ margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>
              <Utensils size={15} style={{ color: '#054625' }} />
              {t('restaurant.dishesIncludedTitle')}
            </h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {selectedMenu.dishes && selectedMenu.dishes.length > 0 ? (
                selectedMenu.dishes.map((dish, index) => (
                  <li key={dish.id || index} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14.5px', color: '#051004' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '999px', background: '#72B744', flexShrink: 0 }} />
                    {typeof dish === 'object' ? dish.name : dish}
                  </li>
                ))
              ) : (
                <li style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>{t('restaurant.noDetailedDish')}</li>
              )}
            </ul>
          </div>

          <div style={{ borderTop: '1px solid rgba(5,16,4,0.08)', paddingTop: '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '26px', letterSpacing: '-0.02em' }}>
              {formatPrice ? formatPrice(selectedMenu.price || 0) : `${selectedMenu.price || 0} €`}
            </span>
            {handleAddToCart && (
              <button
                type="button"
                onClick={() => {
                  handleAddToCart({
                    ...selectedMenu,
                    name: selectedMenu.name,
                    price: selectedMenu.price || 0,
                    quantity: 1
                  });
                  closeMenuPopup();
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', padding: '13px 22px', borderRadius: '999px' }}
              >
                <FaShoppingCart size={15} />
                {t('home.addToCart')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuDetailPopup;
